if (!customElements.get("linked-product-family")) {
  customElements.define(
    "linked-product-family",
    class LinkedProductFamily extends HTMLElement {
      constructor() {
        super();
        this.handleClick = this.handleClick.bind(this);
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
      }

      connectedCallback() {
        if (this.listenersConnected) return;

        this.listenersConnected = true;
        this.addEventListener("click", this.handleClick);
        document.addEventListener("click", this.handleDocumentClick);
        document.addEventListener("keydown", this.handleKeydown);
      }

      disconnectedCallback() {
        if (!this.listenersConnected) return;

        this.listenersConnected = false;
        this.removeEventListener("click", this.handleClick);
        document.removeEventListener("click", this.handleDocumentClick);
        document.removeEventListener("keydown", this.handleKeydown);
      }

      handleClick(event) {
        if (!(event.target instanceof Element)) return;

        const descriptionTrigger = event.target.closest(
          "[data-linked-product-family-description-trigger]",
        );
        if (descriptionTrigger && this.contains(descriptionTrigger)) {
          this.toggleDescription();
          return;
        }

        const descriptionClose = event.target.closest(
          "[data-linked-product-family-description-close]",
        );
        if (descriptionClose && this.contains(descriptionClose)) {
          this.closeDescription({ restoreFocus: true });
          return;
        }

        const link = event.target.closest(
          "[data-linked-product-family-option]",
        );
        if (!link || !this.contains(link)) return;

        const productData = this.getProductData(link);
        if (!productData) return;

        const variant = this.findBestVariant(productData);
        if (!variant) return;

        const url = new URL(productData.url, window.location.origin);
        url.searchParams.set("variant", variant.id);
        link.href = `${url.pathname}${url.search}${url.hash}`;
      }

      handleDocumentClick(event) {
        if (
          !this.descriptionPanel ||
          this.descriptionPanel.hidden ||
          this.contains(event.target)
        )
          return;

        this.closeDescription();
      }

      handleKeydown(event) {
        if (
          event.key !== "Escape" ||
          !this.descriptionPanel ||
          this.descriptionPanel.hidden
        )
          return;

        event.preventDefault();
        this.closeDescription({ restoreFocus: true });
      }

      toggleDescription() {
        if (!this.descriptionPanel || !this.descriptionTrigger) return;

        this.setDescriptionOpen(this.descriptionPanel.hidden);
      }

      closeDescription({ restoreFocus = false } = {}) {
        this.setDescriptionOpen(false, { restoreFocus });
      }

      setDescriptionOpen(isOpen, { restoreFocus = false } = {}) {
        if (!this.descriptionPanel || !this.descriptionTrigger) return;

        this.descriptionPanel.hidden = !isOpen;
        this.descriptionTrigger.setAttribute("aria-expanded", String(isOpen));

        if (!isOpen && restoreFocus) this.descriptionTrigger.focus();
      }

      get descriptionTrigger() {
        return this.querySelector(
          "[data-linked-product-family-description-trigger]",
        );
      }

      get descriptionPanel() {
        return this.querySelector(
          "[data-linked-product-family-description-panel]",
        );
      }

      getProductData(link) {
        const script = link.nextElementSibling;
        if (!script?.matches("[data-linked-product-family-variants]"))
          return null;

        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          console.error(error);
          return null;
        }
      }

      findBestVariant({ optionNames = [], variants = [] }) {
        const currentOptions = this.getCurrentOptions();
        if (!currentOptions.size || !variants.length) return null;

        const scoredVariants = variants
          .map((variant) => ({
            variant,
            score: this.getVariantScore(variant, optionNames, currentOptions),
          }))
          .filter(({ score }) => score > 0)
          .sort((first, second) => {
            if (first.score !== second.score) return second.score - first.score;
            return (
              Number(second.variant.available) - Number(first.variant.available)
            );
          });

        return scoredVariants[0]?.variant || null;
      }

      getCurrentOptions() {
        const productInfo = this.closest("product-info") || document;
        const variantSelects = productInfo.querySelector("variant-selects");
        const selectedOptions = new Map();

        variantSelects
          ?.querySelectorAll("fieldset, .product-form__input--dropdown")
          .forEach((group) => {
            const select = group.querySelector("select");
            const selectedInput =
              group.querySelector("input:checked") ||
              select?.selectedOptions[0];
            const optionName = selectedInput?.dataset.optionName;

            if (optionName && selectedInput.value) {
              selectedOptions.set(
                this.normalize(optionName),
                this.normalize(selectedInput.value),
              );
            }
          });

        return selectedOptions;
      }

      getVariantScore(variant, optionNames, currentOptions) {
        return optionNames.reduce((score, optionName, index) => {
          const selectedValue = currentOptions.get(this.normalize(optionName));
          if (!selectedValue) return score;

          return this.normalize(variant.options[index]) === selectedValue
            ? score + 1
            : score;
        }, 0);
      }

      normalize(value) {
        return String(value).trim().toLowerCase();
      }
    },
  );
}
